/**
 * Page-loading signal — the store behind the top progress bar.
 *
 * Compass has no data router (`BrowserRouter` + `<Routes>`, not
 * `createBrowserRouter`), so there is no `useNavigation()` to tell the shell a
 * route is fetching. Instead each page reports its own in-flight state into
 * this counter and `<RouteProgressBar>` renders whenever the count is > 0.
 *
 * A counter, not a boolean, because pages fire several fetches at once (My
 * Salesforce has three) — the bar must stay up until the last one lands.
 *
 * Wiring, cheapest first:
 *   - `useApi()` reports automatically, so its call sites get the bar free.
 *   - Everything else adds one line: `usePageLoading(loading)`.
 *
 * Module-level store + a `window` event, the same prop-drilling-free pattern
 * `ProcessingBanner` already uses in this codebase.
 */

import { useEffect } from 'react';

const CHANGED_EVENT = 'compass:page-loading-changed';

let inFlight = 0;

function publish(): void {
  window.dispatchEvent(new Event(CHANGED_EVENT));
}

/** How many page-level loads are currently in flight. */
export function getInFlightCount(): number {
  return inFlight;
}

/** Report a load starting. Always pair with `endPageLoad()`. */
export function beginPageLoad(): void {
  inFlight += 1;
  publish();
}

/** Report a load finishing. Never goes below zero, so a double-call from a
 *  cleanup path can't wedge the bar off. */
export function endPageLoad(): void {
  inFlight = Math.max(0, inFlight - 1);
  publish();
}

/** Reset the counter. Tests only. */
export function resetPageLoading(): void {
  inFlight = 0;
  publish();
}

/** Subscribe to count changes. Returns an unsubscribe. */
export function onPageLoadingChanged(fn: () => void): () => void {
  window.addEventListener(CHANGED_EVENT, fn);
  return () => window.removeEventListener(CHANGED_EVENT, fn);
}

/**
 * Report this component's loading flag to the progress bar.
 *
 * ```tsx
 * const [loading, setLoading] = useState(true);
 * usePageLoading(loading);
 * ```
 *
 * Unmounting while still loading releases the count, so navigating away
 * mid-fetch can't leave the bar stuck on.
 */
export function usePageLoading(loading: boolean): void {
  useEffect(() => {
    if (!loading) return;
    beginPageLoad();
    return () => endPageLoad();
  }, [loading]);
}
